import { MenuHeader } from "@/types/menu";
import { normalizeText } from "@/utils/text-format";

/** Remove barras finais duplicadas, garantindo que '/' permaneça como raiz. */
export const normalizePath = (p: string): string => (p || "/").replace(/\/+$/, "") || "/";

/**
 * Filtra menus pela query (case-insensitive), batendo no nome do cabeçalho ou de
 * seus submenus. Quando o match é só em submenus, mantém apenas os que casam.
 * Retorna também os ids dos pais que devem ficar expandidos para revelar o match.
 */
export const filterMenusByQuery = (
  menus: MenuHeader[],
  query: string,
): { filtered: MenuHeader[]; matchedParentIds: string[] } => {
  const q = normalizeText(query);
  if (!q) return { filtered: menus, matchedParentIds: [] };

  const filtered: MenuHeader[] = [];
  const matchedParentIds: string[] = [];

  for (const menu of menus) {
    const subs = menu.subMenus ?? [];
    const nameHit = normalizeText(menu.nome).includes(q);
    const subHits = subs.filter((sub) => normalizeText(sub.nome).includes(q));

    if (nameHit) {
      filtered.push(menu);
      if (subs.length) matchedParentIds.push(menu.id);
    } else if (subHits.length) {
      filtered.push({ ...menu, subMenus: subHits });
      matchedParentIds.push(menu.id);
    }
  }

  return { filtered, matchedParentIds };
};

/**
 * Determina se uma URL de menu corresponde à rota atual.
 * Compara o caminho (prefixo) e, se a URL tiver query params, exige que todos batam.
 */
export const isPathActive = (
  url: string | undefined,
  currentPath: string,
  currentSearch: string,
): boolean => {
  if (!url) return false;

  const [targetPath, targetSearch] = url.split("?");
  const current = normalizePath(currentPath);
  const target = normalizePath(targetPath);

  const pathMatches =
    target === "/" ? current === "/" : current === target || current.startsWith(target + "/");
  if (!pathMatches) return false;
  if (!targetSearch) return true;

  const targetParams = new URLSearchParams(targetSearch);
  const currentParams = new URLSearchParams(currentSearch);
  for (const [key, value] of targetParams) {
    if (currentParams.get(key) !== value) return false;
  }
  return true;
};

/**
 * Dentre itens irmãos, retorna o id do que melhor corresponde à rota atual.
 * Necessário quando uma URL é prefixo de outra (ex.: '/planilhas/atendimento' x
 * '/planilhas/atendimento/agenda'): ambos casam por prefixo, mas só o mais
 * específico (URL mais longa) deve ficar ativo.
 */
export const getBestMatchId = <T extends { id: string; url?: string }>(
  items: T[],
  currentPath: string,
  currentSearch: string,
): string | null => {
  let bestId: string | null = null;
  let bestLen = -1;

  for (const item of items) {
    if (!isPathActive(item.url, currentPath, currentSearch)) continue;
    const len = normalizePath((item.url ?? "").split("?")[0]).length;
    if (len > bestLen) {
      bestLen = len;
      bestId = item.id;
    }
  }

  return bestId;
};
